import React, { Dispatch, SetStateAction } from "react";

import useLocalStorageReference from "./useLocalStorageReference.js";
import useWindowReference from "./useWindowReference.js";

export type StorageKind = "local" | "session";

export interface UseLocalStorageStateOptions {
  /**
   * Which Web Storage area to persist to. Defaults to "local".
   * Pass "session" to use sessionStorage, which is cleared when the tab closes.
   */
  storage?: StorageKind;
}

/**
 * State that mirrors itself into Web Storage (localStorage by default, or
 * sessionStorage via `options.storage`). Falls back to in-memory-only state
 * when storage is unavailable (SSR, private mode, quota exceeded) so callers
 * never need to guard for those cases themselves.
 */
function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
  options?: UseLocalStorageStateOptions,
): [T, Dispatch<SetStateAction<T>>] {
  const storageKind = options?.storage ?? "local";
  const { canUseLocalStorage, localStorage } = useLocalStorageReference();
  const { window } = useWindowReference();

  // Resolve the active Storage area, or undefined when it can't be used.
  // Recomputed only when its inputs change, so the callbacks below stay stable.
  const storage = React.useMemo<Storage | undefined>(() => {
    if (storageKind === "session") {
      return window?.sessionStorage ?? undefined;
    }
    return canUseLocalStorage ? localStorage : undefined;
  }, [storageKind, window, canUseLocalStorage, localStorage]);

  const [value, _setValue] = React.useState<T>(() => {
    try {
      const stored = storage?.getItem(key);
      const parsed: T | null = stored ? JSON.parse(stored) : null;
      return parsed ?? defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const saveToStorage = React.useCallback(
    (next: T) => {
      if (!storage || next === undefined) {
        return;
      }
      try {
        storage.setItem(key, JSON.stringify(next));
      } catch {
        // Quota exceeded or storage disabled — keep the in-memory value only.
      }
    },
    [storage, key],
  );

  const setValue = React.useCallback<Dispatch<SetStateAction<T>>>(
    update => {
      _setValue(current => {
        const next =
          update instanceof Function
            ? (update as (prev: T) => T)(current)
            : update;
        saveToStorage(next);
        return next;
      });
    },
    [saveToStorage],
  );

  React.useEffect(() => {
    saveToStorage(value);
  }, [value, saveToStorage]);

  return [value, setValue];
}

export default useLocalStorageState;
